import {
  Block,
  BlockFooter,
  Button,
  List,
  ListItem,
  Page,
  Popup,
} from "framework7-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteButton from "../components/delete-button";
import PopupNavbar from "../components/popup-navbar";
import { requests } from "../constants/requests";
import { usePopupBackClose } from "../hooks/use-back-close";
import { useDeleteEntry } from "../hooks/use-delete-entry";
import { useEnterKeys } from "../hooks/use-enter-keys";
import { vibrate } from "../hooks/use-haptics";
import { useIsPending } from "../hooks/use-is-pending";
import { useToast } from "../hooks/use-toast";
import {
  createPayment,
  selectMe,
  updatePayment,
} from "../redux/reducer/app-slice";
import { queuedText } from "../utils/outbox";
import {
  NOTE_MAX_LENGTH,
  defaultPayerId,
  descriptionChange,
  noteLength,
  participantName,
  paymentTitleOf,
} from "../utils/expense";
import { formatCents } from "../utils/format";
import { initialSpentAtInput, resolveSpentAt } from "../utils/spent-at-form";
import MoneyInput from "./money-input/money-input";
import NoteInput, { NOTE_INPUT_NAME } from "./note-input";
import PersonPickerPopup from "./person-picker-popup";
import SpentAtInput from "./spent-at-input";

const idOrNull = (value) => (value == null ? null : Number(value));

const getInitialValues = (payment, participants, me) => ({
  fromId: payment?.id
    ? idOrNull(payment.payer)
    : idOrNull(defaultPayerId(me, participants)),
  toId: idOrNull(payment?.receivers?.[0]?.participant_id),
  amount: Number(payment?.amount_in_decimals) || 0,
  spentAt: initialSpentAtInput(payment),
  note: payment?.description ?? "",
});

const validate = ({ fromId, toId, amount, note }) => {
  if (fromId == null) return "Bitte wähle aus, wer gezahlt hat.";
  if (toId == null) return "Bitte wähle aus, wer das Geld bekommen hat.";
  if (fromId === toId) {
    return "Absender und Empfänger müssen verschieden sein.";
  }
  if (!amount) return "Betrag muss größer als 0 sein!";
  if (noteLength(note.trim()) > NOTE_MAX_LENGTH) {
    return `Die Notiz darf höchstens ${NOTE_MAX_LENGTH} Zeichen haben.`;
  }
  return null;
};

// onClose() only once closed, so the content does not flip during the close animation.
const PaymentPopup = ({
  opened,
  onClose = () => {},
  payment,
  participants = [],
}) => {
  const me = useSelector(selectMe);
  const [values, setValues] = useState(() =>
    getInitialValues(payment, participants, me),
  );
  const [picker, setPicker] = useState(null);
  const initialSpentAtRef = useRef(values.spentAt);
  const popupRef = useRef(null);
  // Also blocks a save during the close animation (request state is done).
  const saveSentRef = useRef(false);
  const dispatch = useDispatch();
  const { showError, showInfo } = useToast();
  const { isDeleting, remove } = useDeleteEntry();
  const isSaving = useIsPending(requests.savePayment);
  const isBusy = isSaving || isDeleting;
  const isEditMode = Boolean(payment?.id);
  const backClose = usePopupBackClose(opened);
  useEnterKeys(backClose.id, {
    amount: { hint: "done" },
    "spent-at": { hint: "done" },
    [NOTE_INPUT_NAME]: { hint: "enter" },
  });

  const latest = useRef({});
  latest.current = { participants, me, opened };

  const resetForm = useCallback(() => {
    const current = latest.current;
    const initial = getInitialValues(payment, current.participants, current.me);
    setValues(initial);
    initialSpentAtRef.current = initial.spentAt;
    setPicker(null);
    saveSentRef.current = false;
  }, [payment]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const participantKey = participants.map((p) => p.id).join(",");
  const meId = me?.id ?? null;
  useEffect(() => {
    if (!latest.current.opened) resetForm();
  }, [participantKey, meId, resetForm]);

  const paymentId = payment?.id;
  useEffect(() => {
    if (opened && !paymentId) {
      const now = initialSpentAtInput(null);
      setValues((prev) => ({ ...prev, spentAt: now }));
      initialSpentAtRef.current = now;
    }
  }, [opened, paymentId]);

  const set = (key) => (value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const closePopup = () => popupRef.current?.close();

  const handleSubmit = () => {
    if (saveSentRef.current || isBusy) return;
    const invalid = validate(values);
    if (invalid) {
      showError(invalid);
      return;
    }
    const { spentAt, error: spentAtError } = resolveSpentAt({
      value: values.spentAt,
      initialValue: initialSpentAtRef.current,
      isEdit: isEditMode,
    });
    if (spentAtError) {
      showError(spentAtError);
      return;
    }
    const { note, ...rest } = values;
    const data = {
      ...rest,
      title: isEditMode ? paymentTitleOf(payment) : "",
      spentAt,
      description: descriptionChange(note, payment),
    };
    const onSuccess = (group, { queued = false } = {}) => {
      vibrate();
      const done = isEditMode ? "Zahlung gespeichert" : "Zahlung erfasst";
      showInfo(queued ? queuedText(done) : done);
      closePopup();
    };
    const onError = (message) => {
      saveSentRef.current = false;
      showError(message);
    };
    saveSentRef.current = true;
    dispatch(
      isEditMode
        ? updatePayment(payment.id, data, onSuccess, onError)
        : createPayment(data, onSuccess, onError),
    );
  };

  const handleDelete = () => {
    if (!isEditMode || isSaving) return;
    remove(payment, participants, closePopup);
  };

  const fromName = participantName(participants, values.fromId, "");
  const toName = participantName(participants, values.toId, "");
  const summary =
    fromName && toName && values.fromId !== values.toId && values.amount > 0
      ? `${fromName} hat ${toName} ${formatCents(values.amount)} gezahlt.`
      : "Wer hat wem Geld gegeben (z.B. zum Ausgleich der Schulden)?";

  return (
    <Popup
      {...backClose}
      className="payment-popup"
      opened={opened}
      onPopupOpened={(popup) => {
        popupRef.current = popup;
      }}
      onPopupClosed={() => {
        resetForm();
        onClose();
      }}
    >
      <Page>
        <PopupNavbar
          title={isEditMode ? "Zahlung bearbeiten" : "Zahlung erfassen"}
        />

        <Block>
          <List strong outline inset dividers className="payment-people">
            <ListItem
              className="payment-from"
              title="Von"
              link
              onClick={() => setPicker("from")}
            >
              {fromName || "Wer hat gezahlt?"}
            </ListItem>
            <ListItem
              className="payment-to"
              title="An"
              link
              onClick={() => setPicker("to")}
            >
              {toName || "Wer hat das Geld bekommen?"}
            </ListItem>
          </List>
          <List strongIos insetIos outlineIos className="payment-form">
            <MoneyInput value={values.amount} onChange={set("amount")} />
            <SpentAtInput value={values.spentAt} onChange={set("spentAt")} />
            <NoteInput value={values.note} onChange={set("note")} />
          </List>
          <BlockFooter className="payment-summary">{summary}</BlockFooter>
        </Block>

        <Block inset className="payment-actions">
          <Button fill disabled={isBusy} onClick={handleSubmit}>
            Speichern
          </Button>
          {isEditMode && (
            <DeleteButton
              key="delete"
              className="payment-delete-button"
              busy={isDeleting}
              disabled={isSaving}
              onClick={handleDelete}
            >
              Zahlung löschen
            </DeleteButton>
          )}
        </Block>

        {/* Popup rule: one always-rendered wrapper per nested popup – see AGENTS.md. */}
        <div className="payment-from-popup-wrapper">
          <PersonPickerPopup
            className="payment-from-popup"
            title="Wer hat gezahlt?"
            opened={picker === "from"}
            onClose={() => setPicker(null)}
            participants={participants}
            selectedId={values.fromId}
            disabledId={values.toId}
            disabledHint="bekommt"
            onSelect={(id) => {
              set("fromId")(idOrNull(id));
              setPicker(null);
            }}
          />
        </div>
        <div className="payment-to-popup-wrapper">
          <PersonPickerPopup
            className="payment-to-popup"
            title="Wer hat das Geld bekommen?"
            opened={picker === "to"}
            onClose={() => setPicker(null)}
            participants={participants}
            selectedId={values.toId}
            disabledId={values.fromId}
            disabledHint="zahlt"
            onSelect={(id) => {
              set("toId")(idOrNull(id));
              setPicker(null);
            }}
          />
        </div>
      </Page>
    </Popup>
  );
};

export default PaymentPopup;
